import React, { forwardRef } from 'react';
import { Avatar as MuiAvatar, AvatarProps as MuiAvatarProps } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { capitalize } from '@mui/material/utils';

export type AvatarColorType =
  | 'orange'
  | 'lightBlue'
  | 'green'
  | 'primary'
  | 'primaryLight'
  | 'purple'
  | 'darkBlue'
  | 'secondary'
  | 'yellow'
  | 'gray'
  | 'mediumGray';

export interface AvatarProps extends MuiAvatarProps {
  dark?: boolean;
  medium?: boolean;
  small?: boolean;
  verySmall?: boolean;
  fullWidth?: boolean;
  color?: AvatarColorType;
  sx?: any;
  width?: number;
  height?: number;
}

interface StyleProps {
  width?: number;
  height?: number;
}

const useStyles = makeStyles<StyleProps>()((theme, props: StyleProps) => ({
  root: {
    width: props.width || theme.spacing(7),
    height: props.height || theme.spacing(7),
    '&.colorOrange': {
      backgroundColor: alpha(theme.palette.custom.orange.main, 0.2),
      color: theme.palette.custom.orange.main,
      '&.dark': {
        backgroundColor: theme.palette.custom.orange.main,
      },
    },
    '&.colorLightBlue': {
      backgroundColor: alpha(theme.palette.primary.light, 0.2),
      color: theme.palette.primary.light,
      '&.dark': {
        backgroundColor: theme.palette.primary.light,
      },
    },
    '&.colorGreen': {
      backgroundColor: alpha(theme.palette.custom.green.main, 0.2),
      color: theme.palette.custom.green.main,
      '&.dark': {
        backgroundColor: theme.palette.custom.green.main,
      },
    },
    '&.colorPrimary': {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      color: theme.palette.primary.main,
      '&.dark': {
        backgroundColor: theme.palette.primary.main,
      },
    },
    '&.colorPrimaryLight': {
      backgroundColor: alpha(theme.palette.primary.light, 0.2),
      color: theme.palette.primary.light,
      '&.dark': {
        backgroundColor: theme.palette.primary.light,
      },
    },
    '&.colorPurple': {
      backgroundColor: alpha(theme.palette.custom.purple.main, 0.2),
      color: theme.palette.custom.purple.main,
      '&.dark': {
        backgroundColor: theme.palette.custom.purple.main,
      },
    },
    '&.colorDarkBlue': {
      backgroundColor: alpha(theme.palette.custom.darkBlue.main, 0.2),
      color: theme.palette.custom.darkBlue.main,
      '&.dark': {
        backgroundColor: theme.palette.custom.darkBlue.main,
      },
    },
    '&.colorSecondary': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.2),
      color: theme.palette.secondary.main,
      '&.dark': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    '&.colorYellow': {
      backgroundColor: alpha(theme.palette.custom.yellow.main, 0.2),
      color: theme.palette.custom.yellow.main,
      '&.dark': {
        backgroundColor: theme.palette.custom.yellow.main,
      },
    },
    '&.colorMediumGray': {
      backgroundColor: theme.palette.custom.gray.medium,
      color: theme.palette.custom.white,
      '&.dark': {
        backgroundColor: theme.palette.custom.gray.medium,
      },
    },
    '&.colorGray': {
      backgroundColor: theme.palette.custom.gray.main,
      color: theme.palette.custom.white,
      '&.dark': {
        backgroundColor: theme.palette.custom.gray.main,
      },
    },
    '&.medium': {
      width: theme.spacing(6),
      height: theme.spacing(6),
    },
    '&.small': {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
    '&.verySmall': {
      width: 24,
      height: 24,
    },
    '&.fullWidth': {
      fontSize: 72,
      width: '100%',
      height: '100%',
    },
    '&.dark': {
      color: 'white',
      backgroundColor: alpha(theme.palette.text.primary, 0.2),
    },
  },
}));

// TODO:
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const Avatar: React.FC<React.PropsWithChildren<AvatarProps>> = forwardRef((props, ref) => {
  const { classes, cx } = useStyles(props, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    props,
  });
  const { dark = false, medium = false, small = false, verySmall = false, fullWidth = false, color, ...rest } = props;
  return (
    <MuiAvatar
      {...rest}
      ref={ref}
      classes={classes}
      className={cx(color && `color${capitalize(color)}`, { dark, small, fullWidth, verySmall, medium })}
    />
  );
});

(Avatar as any).muiName = 'Avatar';

export default Avatar;
